import { AssessmentConfiguration, ConfigurationDescription, DescriptionItem, DescriptionSubItem } from '@amzn/awscat-portfolio-assessment-types';

import { constants } from '../../common/Localization';
import { isVmwareEnabled, RegionsConfig,VMwareInstanceTypes } from '../../common/regions';
import { format } from '../../common/util';
import { AWS_EC2_PRICING, AWS_EC2_RIGHT_SIZE, AWS_LABOR_SUPPORT, AWS_NETWORK, AWS_OPTIMIZATION_LICENSING, 
    AWS_RDS_LICENSE, AWS_RDS_PRICING, AWS_RDS_RIGHT_SIZE, AWS_STORAGE, DATABASE_LICENSE, DATABASE_MIGRATION_EFFORT, 
    GENERAL_STORAGE, MAINFRAME, MAINFRAME_MIGRATION_COST, MAINFRAME_ON_PREM, MIGRATION_LEVEL_OF_EFFORT, 
    MIGRATION_TOOLS_AND_SCHEDULE, ON_PREM_ADMIN, ON_PREM_DISCOUNT, ON_PREM_IT_OPERATION, ON_PREM_NETWORK, 
    ON_PREM_POWER, ON_PREM_SERVER_COST, ON_PREM_SOFTWARE, ON_PREM_STORAGE, TCO_ASSUMPTIONS, VMWARE_CLOUD 
} from './constants';

/**
 * Used to track whether errors exist in each form
 */
export interface TcoAssumptionInvalidFormMapping {
    [sectionId: string]: boolean;
};
export const getDefaultSectionAndFields = (configuration: DescriptionItem[]): {
    defaultSections: string[],
    defaultFields: string[],
    defaultFormMapping: TcoAssumptionInvalidFormMapping,
} => {
    const defaultVisibleSections: string[] = [];
    const defaultVisibleFields: string[] = [];
    const formMapping: TcoAssumptionInvalidFormMapping = {};
    configuration.forEach((section: DescriptionItem) => {
        if (!section.uiHidden) {
            defaultVisibleSections.push(section.identifier);
            section.properties.forEach((field: DescriptionSubItem) => {
                if (!field.uiHidden) {
                    defaultVisibleFields.push(field.identifier);
                }
            });
        }
        formMapping[section.identifier] = true;
    });

    return {
        defaultSections: defaultVisibleSections,
        defaultFields: defaultVisibleFields,
        defaultFormMapping: formMapping,
    };
};

/**
 * Only display specific sections of assumption descriptions
 * @param propertiesList Assumption Description items lists
 * @param visibleSections Assumption Description Section Ids
 * @returns updated assumption descriptions
 */
export const showSections = (propertiesList: DescriptionItem[], visibleSections: string[]): DescriptionItem[] => {
    propertiesList.forEach((section: DescriptionItem) => {
        section.uiHidden = !visibleSections.includes(section.identifier);
    });
    return propertiesList;
};

/**
 * Only display specific fields of assumption descriptions
 * @param propertiesList Assumption Description items lists
 * @param visibleSections Assumption Description Section Ids
 * @param visibleFields Assumption Field Id
 */
export const showFields = (propertiesList: DescriptionItem[], visibleSections: string[], visibleFields: string[]): void => {
    propertiesList.forEach(section => {
        if (visibleSections.includes(section.identifier)) {
            section.uiHidden = false;
            section.properties.forEach(field => {
                field.uiHidden = !visibleFields.includes(field.identifier);
            });
        } else {
            section.uiHidden = true;
        }
    });
};

/**
 * Attach alert messages to assumption description
 * @param propertiesList Assumption Description items lists
 * @param sectionIdentifier Assumption Description Section Id
 * @param fieldIdentifier Assumption Field Id
 * @param alertMsgs Alert messages to be attached
 */
export const addAlertsToFormField = (propertiesList: DescriptionItem[], sectionIdentifier: string, fieldIdentifier: string, alertMsgs: string[]): void => {
    const property = getConfigurationByIdentifier(propertiesList, sectionIdentifier, fieldIdentifier);
    if (property) {
        property.alertMsgs = alertMsgs;
    }
};

/**
 * Set uiHidden on specific assumption sub description item
 * @param propertiesList Assumption Description items lists
 * @param sectionIdentifier Assumption Description Section Id
 * @param fieldIdentifier Assumption Field Id
 * @param uiHidden true if hide, false if display
 */
export const setConfigurationUiHiddenByIdentifier =
    (propertiesList: DescriptionItem[], sectionIdentifier: string, fieldIdentifier: string, uiHidden: boolean): void => {
        const property = getConfigurationByIdentifier(propertiesList, sectionIdentifier, fieldIdentifier);
        if (property) {
            property.uiHidden = uiHidden;
        }
    };

const getConfigurationByIdentifier =
    (propertiesList: DescriptionItem[], sectionIdentifier: string, fieldIdentifier: string): DescriptionSubItem | undefined => {
        const Section = propertiesList.find(function (property) {
            return property.identifier === sectionIdentifier;
        });
        const property = Section?.properties.find(function (property) {
            return property.identifier === fieldIdentifier;
        });
        return property;
    };

/**
 * Check if a region support relocate migration strategy
 * @param region region code
 * @returns true if support, false if not support
 */
export const regionSupportRelocate = (region: string | undefined, regions: RegionsConfig): boolean => {
    if (!region) {
        return false;
    }

    if (!regions[region]) {
        return false;
    }

    return isVmwareEnabled(regions[region]);
};

/**
 * Determines if the given instanceType is supported in the given region
 * @param region the region to check compatability with
 * @param instanceType the instance type to check support for in the given region
 * @returns true if vmware is enabled and the instance type is supported in the given region
 */
export const isVmwareInstanceTypeSupported = (region: string, instanceType: string, regions: RegionsConfig): boolean => {
    return regionSupportRelocate(region, regions) 
        && (region in regions) 
        && (regions[region].supportedVmwareInstanceTypes.includes(instanceType as VMwareInstanceTypes));
};

/**
 * If there is an error for the given region/instancetype configuration, return it. Else return undefined.
 * @param region the region to compare compatibility with
 * @param instanceType The type of the instance to check compatibility with
 * @param regions the list of regions and their supported instance types
 * @returns the error message if there is one, otherwise undefined
 */
export const getVMwareInstanceTypeAlert = (region: string, instanceType: string, regions: RegionsConfig): string | undefined => {
    if (!isVmwareInstanceTypeSupported(region, instanceType, regions)) {
        // only add instance compatability alert if vmware is supported, but not that instance type
        return format(constants.ALERT_MSG_INSTANCE_TYPE, [instanceType, regions[region].label]);
    }
    return undefined;
};

/**
 * Gets any warnings related to the updated region(or how the vmware instance type is supported in the selection region)
 * @param newRegion the region which will se set then the user hits save
 * @param oldRegion the region used in the assessment before the current selection updated anything
 * @param instanceType the vmware instance type recommended
 * @param regions the list of regions and their supported instance types
 * @returns the error messages if there is one, otherwise empty array
 */
export const getRegionAlerts = (newRegion: string, oldRegion: string, instanceType: string, regions: RegionsConfig): string[] => {
    const alertMsgs: string[] = [];
    
    if (!regionSupportRelocate(newRegion, regions) && regionSupportRelocate(oldRegion, regions)) {
        const alertMsg = format(constants.ALERT_MSG_RELOCATE, [regions[newRegion].label]);
        alertMsgs.push(alertMsg);
    } else {
        // only add instance compatability alert if vmware is supported, but not that instance type
        const alertMsg = getVMwareInstanceTypeAlert(newRegion, instanceType, regions);
        alertMsg && alertMsgs.push(alertMsg);
    }

    if (isSwitchToChina(newRegion, oldRegion)) {
        alertMsgs.push(constants.ALERT_MSG_TO_CHINA);
    } 

    if (isSwitchFromChina(newRegion, oldRegion)) {
        alertMsgs.push(constants.ALERT_MSG_FROM_CHINA);
    }

    return alertMsgs;
};

export const isSwitchToChina = (newRegion: string, oldRegion: string): boolean => {
    return (!oldRegion.startsWith('cn') && newRegion.startsWith('cn'));
};

export const isSwitchFromChina = (newRegion: string, oldRegion: string): boolean => {
    return (oldRegion.startsWith('cn') && !newRegion.startsWith('cn'));
};

export const getRelevantAssumptions = (location: string): string[] | null => {
    const locationParts: string[] = location.split('/');
    if (!locationParts.includes('tco')) {
        return null;
    }

    if (locationParts.includes('mainframe-summary')) {
        return [TCO_ASSUMPTIONS, MAINFRAME, MAINFRAME_ON_PREM, MAINFRAME_MIGRATION_COST, MIGRATION_TOOLS_AND_SCHEDULE,
            AWS_EC2_PRICING, AWS_STORAGE, GENERAL_STORAGE, AWS_RDS_PRICING, AWS_RDS_RIGHT_SIZE, AWS_LABOR_SUPPORT];
    } else if (locationParts.includes('sql-server-optimization')) {
        return [AWS_EC2_PRICING, AWS_EC2_RIGHT_SIZE, AWS_OPTIMIZATION_LICENSING, AWS_RDS_RIGHT_SIZE, AWS_RDS_LICENSE];
    } else if (locationParts.includes('aws')) {
        if (locationParts.includes('database-costs')) {
            return [TCO_ASSUMPTIONS, DATABASE_LICENSE, AWS_RDS_PRICING, AWS_RDS_RIGHT_SIZE];
        } else if (locationParts.includes('database-recommendations')) {
            return [TCO_ASSUMPTIONS, AWS_RDS_PRICING, AWS_RDS_RIGHT_SIZE];
        } else if (locationParts.includes('it-labor-support-costs')) {
            return [TCO_ASSUMPTIONS, AWS_LABOR_SUPPORT];
        } else if (locationParts.includes('storage-network-costs')) {
            return [TCO_ASSUMPTIONS, AWS_NETWORK, AWS_STORAGE, GENERAL_STORAGE];
        } else if (locationParts.includes('server-costs')) {
            return [TCO_ASSUMPTIONS, DATABASE_LICENSE, ON_PREM_SOFTWARE, AWS_EC2_RIGHT_SIZE, AWS_EC2_PRICING, AWS_RDS_RIGHT_SIZE, VMWARE_CLOUD];
        } else if (locationParts.includes('server-recommendations')) {
            return [TCO_ASSUMPTIONS, AWS_EC2_RIGHT_SIZE, AWS_EC2_PRICING, AWS_RDS_RIGHT_SIZE, VMWARE_CLOUD];
        }
    } else if (locationParts.includes('on-prem')) {
        if (locationParts.includes('it-labor-costs')) {
            return [TCO_ASSUMPTIONS, ON_PREM_SERVER_COST, ON_PREM_ADMIN];
        } else if (locationParts.includes('server-costs')) {
            return [TCO_ASSUMPTIONS, ON_PREM_SERVER_COST, ON_PREM_IT_OPERATION, ON_PREM_DISCOUNT, ON_PREM_POWER, ON_PREM_SOFTWARE];
        } else if (locationParts.includes('database-costs')) {
            return [TCO_ASSUMPTIONS, ON_PREM_SERVER_COST, ON_PREM_IT_OPERATION, GENERAL_STORAGE, ON_PREM_STORAGE, DATABASE_LICENSE];
        } else if (locationParts.includes('storage-network-costs')) {
            return [TCO_ASSUMPTIONS, ON_PREM_SERVER_COST, ON_PREM_IT_OPERATION, GENERAL_STORAGE, ON_PREM_STORAGE, ON_PREM_NETWORK];
        }
    } else if (locationParts.includes('migration-strategy')) {
        return [TCO_ASSUMPTIONS, MIGRATION_TOOLS_AND_SCHEDULE, MIGRATION_LEVEL_OF_EFFORT];
    } else if (locationParts.includes('migration')) {
        if (locationParts.includes('summary')) {
            return [TCO_ASSUMPTIONS, MIGRATION_TOOLS_AND_SCHEDULE, MIGRATION_LEVEL_OF_EFFORT, DATABASE_MIGRATION_EFFORT];
        } else if (locationParts.includes('cash-flow')) {
            return [TCO_ASSUMPTIONS, MIGRATION_TOOLS_AND_SCHEDULE, MIGRATION_LEVEL_OF_EFFORT];
        }
    }
    return null;
};

// Constants that are used for assumption save & load
export const ASSUMPTION_PREFIX = '/Assumptions/';
export const ASSUMPTION_TYPE = 'Assumptions';

export const generateDefaultAssumption = (descriptions: ConfigurationDescription): AssessmentConfiguration => {
    const defaultConfigs = descriptions.configuration.properties.reduce((config, descItem: DescriptionItem) => ({
        ...config,
        [descItem.identifier]: descItem.properties.reduce((subConfig, subItem) => ({...subConfig, [subItem.identifier]: subItem.defaultValue}), {}),
    }), {} as AssessmentConfiguration);

    return defaultConfigs;
};
